import { getBackendErrorMessage } from "@/api/errors";
import { erIndberetningModtaget } from "./afgoerelseIndberetning";
import { AlertMessage } from "@/components/AlertMessage";
import Button from "@/components/bricks/button/Button";
import ValidationErrorMessage from "@/components/formcontrols/ValidationErrorMessage";
import { FormikDatePicker } from "@/components/formcontrols/DatePicker";
import { SelectFormik } from "@/components/formcontrols/Select";
import FristForhaandsvisning from "./FristForhaandsvisning";
import { DokumentationGaelderFraFelt } from "./DokumentationGaelderFraFelt";
import { erEfterAfgoerelsesdato } from "./dokumentationGaelderFra";
import { FormikKodeSelect } from "@/components/formcontrols/KodeSelect";
import IsRequired from "@/components/formcontrols/common/IsRequired";
import EditIcon from "@/components/icons/EditIcon";
import Modal, {
  ModalContext,
  ModalProvider,
  ModalTrigger,
} from "@/components/Modal/Modal";
import Spinner from "@/components/spinner/Spinner";
import { StickyFooter } from "@/components/stickyFooter/modalStickyFooter";
import {
  usePersonPaalaeg,
  useUpdatePersonPaalaeg,
} from "@/hooks/person-hooks/usePersonPaalaeg";
import { dateToyyyyMMdd } from "@/util/date";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext, useMemo, useState } from "react";
import * as Yup from "yup";
import {
  FRIST_LAENGDE_MAANEDER,
  PERIODE_LAENGDER,
  PERIODE_LAENGDE_OPTIONS,
} from "./injunctionPeriode";
import { YupNumberOfLessonsRequired } from "../../../../../util/yup";

import type {
  EfteruddannelseIndberetningStatusId,
  PersonPaalaegDTO,
  UpdatePersonPaalaegFields,
} from "@/api/person/paalaeg";

interface EditPaalaegFormValues {
  paakraevedeLektioner: string;
  kendelseDato: Date | null;
  dokumentationGaelderFra: Date | null;
  periodeLaengdeMaaneder: string;
  bemaerkning: string;
  efteruddannelseIndberetningStatus: EfteruddannelseIndberetningStatusId | "";
}

interface EditPaalaegModalProps {
  personId: string;
  paalaegId: string;
}

const UPDATE_ERROR_FALLBACK = "Kunne ikke opdatere pålæg.";

const validationSchema = Yup.object({
  paakraevedeLektioner: YupNumberOfLessonsRequired(),
  kendelseDato: Yup.date().nullable(),
  dokumentationGaelderFra: Yup.date()
    .nullable()
    .test(
      "ikkeEfterAfgoerelse",
      "Kan ikke ligge efter kendelsesdatoen",
      (value, context) =>
        !erEfterAfgoerelsesdato(value ?? null, context.parent.kendelseDato),
    ),
  periodeLaengdeMaaneder: Yup.string()
    .required("Indfrielsesperiodens længde skal vælges")
    .oneOf([...PERIODE_LAENGDER], "Ugyldig længde"),
  efteruddannelseIndberetningStatus: Yup.string().required(
    "Indberetningsstatus skal vælges",
  ),
});

const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildPatchChanges = (
  values: EditPaalaegFormValues,
  data: PersonPaalaegDTO,
): UpdatePersonPaalaegFields => {
  const changes: UpdatePersonPaalaegFields = {};

  if (Number(values.paakraevedeLektioner) !== data.paakraevedeLektioner) {
    changes.paakraevedeLektioner = Number(values.paakraevedeLektioner);
  }
  const nyKendelsesdato = values.kendelseDato
    ? dateToyyyyMMdd(values.kendelseDato)
    : null;
  if (nyKendelsesdato !== (data.kendelseDato ?? null)) {
    changes.kendelseDato = values.kendelseDato;
  }
  const nyDokumentationGaelderFra = values.dokumentationGaelderFra
    ? dateToyyyyMMdd(values.dokumentationGaelderFra)
    : null;
  if (nyDokumentationGaelderFra !== (data.dokumentationGaelderFra ?? null)) {
    changes.dokumentationGaelderFra = nyDokumentationGaelderFra;
  }
  const nyLaengde = Number(values.periodeLaengdeMaaneder);
  if (nyLaengde !== (data.periodeLaengdeMaaneder ?? null)) {
    changes.periodeLaengdeMaaneder = nyLaengde;
  }
  if ((values.bemaerkning.trim() || null) !== (data.bemaerkning ?? null)) {
    changes.bemaerkning = values.bemaerkning.trim() || null;
  }

  if (
    values.efteruddannelseIndberetningStatus !==
    (data.indberetning?.efteruddannelseIndberetningStatus ?? "")
  ) {
    changes.efteruddannelseIndberetningStatus =
      values.efteruddannelseIndberetningStatus as EfteruddannelseIndberetningStatusId;
  }

  return changes;
};

const EditPaalaegForm = ({ personId, paalaegId }: EditPaalaegModalProps) => {
  const { setModalOpen } = useContext(ModalContext);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { data, isLoading, error } = usePersonPaalaeg({
    personId,
    paalaegId,
  });
  const { mutate: updatePaalaeg, isPending } = useUpdatePersonPaalaeg();

  const initialValues = useMemo<EditPaalaegFormValues | null>(() => {
    if (!data) return null;

    return {
      paakraevedeLektioner: String(data.paakraevedeLektioner),
      kendelseDato: parseDate(data.kendelseDato),
      dokumentationGaelderFra: parseDate(data.dokumentationGaelderFra),
      periodeLaengdeMaaneder: data.periodeLaengdeMaaneder
        ? String(data.periodeLaengdeMaaneder)
        : "",
      bemaerkning: data.bemaerkning ?? "",
      efteruddannelseIndberetningStatus:
        data.indberetning?.efteruddannelseIndberetningStatus ?? "",
    };
  }, [data]);

  // Er indberetningen modtaget, er afgørelsen dokumentation og kan ikke længere rettes.
  // Kun indberetningens status må ændres — fx til Undersøges. Domænet spærrer det og svarer
  // 400; her handler det om ikke at tilbyde felter, der alligevel bliver afvist.
  const erModtaget = erIndberetningModtaget(
    data?.indberetning?.efteruddannelseIndberetningStatus,
  );

  const formik = useFormik<EditPaalaegFormValues>({
    initialValues: initialValues ?? {
      paakraevedeLektioner: "",
      kendelseDato: null,
      dokumentationGaelderFra: null,
      periodeLaengdeMaaneder: "",
      bemaerkning: "",
      efteruddannelseIndberetningStatus: "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      if (!data || !values.efteruddannelseIndberetningStatus) {
        return;
      }

      const changes = buildPatchChanges(values, data);

      if (Object.keys(changes).length === 0) {
        setModalOpen(false);
        return;
      }

      setSubmitError(null);

      updatePaalaeg(
        {
          personId,
          paalaegId,
          changes,
        },
        {
          onSuccess: () => setModalOpen(false),
          onError: (updateError) =>
            setSubmitError(
              getBackendErrorMessage(updateError, UPDATE_ERROR_FALLBACK),
            ),
        },
      );
    },
  });

  if (isLoading) {
    return (
      <div className="c-modal-container p-4">
        <Spinner label="Henter pålæg ..." />
      </div>
    );
  }

  if (error || !data || !initialValues) {
    return (
      <div className="c-modal-container p-4 text-danger">
        Kunne ikke hente pålæg.
      </div>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <div className="c-modal-container">
          <div className="c-item-list c-modal-container-inner">
            {erModtaget && (
              <div className="flex-100">
                <div className="alert alert-info mb-0" role="status">
                  Indberetningen er modtaget, og pålægget kan derfor ikke
                  ændres. Kun indberetningsstatussen kan rettes — fx til
                  Undersøges eller Indbragt for advokatnævnet.
                </div>
              </div>
            )}
            <div className="flex-100">
              <label className="form-label" htmlFor="paakraevedeLektioner">
                Antal lektioner
                <IsRequired />
              </label>
              <input
                autoComplete="off"
                className={`form-control${
                  formik.touched.paakraevedeLektioner &&
                  formik.errors.paakraevedeLektioner
                    ? " is-invalid"
                    : ""
                }`}
                disabled={erModtaget}
                id="paakraevedeLektioner"
                min="0"
                step="any"
                type="number"
                value={formik.values.paakraevedeLektioner}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
              <ValidationErrorMessage
                touched={formik.touched.paakraevedeLektioner}
                errorMessage={formik.errors.paakraevedeLektioner}
              />
            </div>
            <div className="flex-100">
              <SelectFormik
                data={PERIODE_LAENGDE_OPTIONS}
                formik={formik}
                config={{
                  component: "select",
                  id: "periodeLaengdeMaaneder",
                  label: "Indfrielsesperiodens længde",
                  placeholder: "Vælg længde...",
                  isRequired: true,
                }}
              />
              <p className="text-muted small mb-0 mt-1">
                Fristens længde er fast {FRIST_LAENGDE_MAANEDER} måneder efter
                periodens udløb. Ændrer du længden, beregnes begge datoer om.
              </p>
            </div>
            <div className="flex-100">
              <FristForhaandsvisning
                type="paalaeg"
                personId={personId}
                kendelseDato={
                  formik.values.kendelseDato
                    ? dateToyyyyMMdd(formik.values.kendelseDato)
                    : null
                }
                periodeLaengdeMaaneder={
                  formik.values.periodeLaengdeMaaneder
                    ? Number(formik.values.periodeLaengdeMaaneder)
                    : null
                }
              />
            </div>
            <div className="flex-100">
              <label className="form-label" htmlFor="bemaerkning">
                Bemærkning
              </label>
              <textarea
                className="form-control"
                disabled={erModtaget}
                id="bemaerkning"
                rows={3}
                value={formik.values.bemaerkning}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex-100">
              <FormikDatePicker
                formik={formik}
                config={{
                  component: "datepicker",
                  id: "kendelseDato",
                  label: "Kendelsesdato",
                  initialValue: "",
                  isRequired: false,
                }}
              />
            </div>
            <div className="flex-100">
              <DokumentationGaelderFraFelt
                formik={formik}
                afgoerelse="pålæg"
                disabled={erModtaget}
              />
            </div>
            <div className="flex-100">
              <FormikKodeSelect
                formik={formik}
                config={{
                  component: "kode-select",
                  id: "efteruddannelseIndberetningStatus",
                  label: "Indberetningsstatus",
                  kodeListeType: "EfteruddannelseIndberetningStatusKode",
                  placeholder: "Status",
                  initialValue: initialValues.efteruddannelseIndberetningStatus,
                  filter:
                    initialValues.efteruddannelseIndberetningStatus !==
                    "FristOrdinaer"
                      ? ["FristOrdinaer"]
                      : undefined,
                  isRequired: true,
                }}
                value={formik.values.efteruddannelseIndberetningStatus?.toString()}
                onChange={(kode) => {
                  const value = kode
                    ? ((kode.type === "enum" ? kode.id : kode.nr.toString()) ??
                      "")
                    : "";
                  formik.setTouched({
                    ...formik.touched,
                    efteruddannelseIndberetningStatus: true,
                  });
                  formik.setFieldValue(
                    "efteruddannelseIndberetningStatus",
                    value,
                    true,
                  );
                }}
              />
            </div>
          </div>
          {submitError && (
            <div className="c-modal-container-inner mb-2">
              <AlertMessage type="danger" warningIcon>
                <p className="mb-0">{submitError}</p>
              </AlertMessage>
            </div>
          )}
          <StickyFooter>
            <Button
              color="secondary"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              Annuller
            </Button>
            <div className="d-flex gap-2 align-items-center">
              {isPending && <Spinner size={24} />}
              <Button color="primary" disabled={isPending} type="submit">
                Gem
              </Button>
            </div>
          </StickyFooter>
        </div>
      </Form>
    </FormikProvider>
  );
};

export const EditPaalaegModal = ({
  personId,
  paalaegId,
}: EditPaalaegModalProps) => (
  <ModalProvider>
    <ModalTrigger asChild className="btn btn-primary btn--transparent has-icon">
      <button id="efteruddannelse-rediger-paalaeg" type="button">
        <EditIcon height={16} />
        Rediger
      </button>
    </ModalTrigger>
    <Modal title="Rediger pålæg">
      <EditPaalaegForm personId={personId} paalaegId={paalaegId} />
    </Modal>
  </ModalProvider>
);
